import { useState, useEffect, useCallback } from 'react';
import { fetchTokenStatus, setToken, clearToken, getToken } from '../api/client';

export type AuthState = 'loading' | 'setup' | 'login' | 'authenticated';

export function useAuth() {
  const [state, setState] = useState<AuthState>('loading');
  const [role, setRole] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    try {
      const status = await fetchTokenStatus();

      if (!status.hasUsers) {
        setState('setup');
        return;
      }

      if (status.authenticated) {
        setState('authenticated');
        setRole(status.role ?? null);
        setEmail(status.email ?? null);
      } else {
        // Has users but we're not authenticated
        if (getToken()) {
          // Token exists but is invalid/revoked
          clearToken();
        }
        setState('login');
      }
    } catch {
      // API unreachable — show login
      setState('login');
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    const onLogout = () => {
      setState('login');
      setRole(null);
      setEmail(null);
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  const login = useCallback(async (rawToken: string) => {
    setToken(rawToken);
    const status = await fetchTokenStatus();
    if (status.authenticated) {
      setState('authenticated');
      setRole(status.role ?? null);
      setEmail(status.email ?? null);
      return true;
    }
    clearToken();
    return false;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setState('login');
    setRole(null);
    setEmail(null);
  }, []);

  const setupComplete = useCallback(async (rawToken: string) => {
    setToken(rawToken);
    setState('authenticated');
    // Re-fetch to get role
    const status = await fetchTokenStatus();
    setRole(status.role ?? null);
    setEmail(status.email ?? null);
  }, []);

  return { state, role, email, login, logout, setupComplete, checkStatus };
}
